import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sortDayKeysDesc } from '../photoUpload/dayBuckets';
import {
  MODE_ALL,
  SELECTION_ALL,
  daysForSelection,
  normalizeSelection,
  selectionFromDays,
  selectionsEqual,
} from '../utils/daySelection';

// Which capture days the next compile should consume (#385). Owned by
// FootageTab because two siblings need it: PhotoDayGrid renders the
// checkboxes, PhotoCompileCta costs the choice and sends it.
//
// The wire selection is the state, and the selected-day Set is DERIVED from
// it against the current coverage. That ordering matters: coverage streams in
// after first paint, so a Set captured up-front would be empty forever, and
// keeping `{mode:'all'}` as a mode (rather than "every key I saw at mount")
// means days uploaded later stay included without the user re-picking them.
export function useDaySelection(coverage, persistedSelection) {
  const coverageDays = coverage?.days;

  // Display order — newest first, 'unknown' last — shared with PhotoDayGrid so
  // shift-click ranges follow what the user actually sees.
  const allDayKeys = useMemo(() => {
    const days = coverageDays || {};
    return sortDayKeysDesc(Object.keys(days).filter((k) => (days[k]?.photoCount || 0) > 0));
  }, [coverageDays]);

  const persisted = useMemo(
    () => normalizeSelection(persistedSelection),
    [persistedSelection],
  );

  const [selection, setSelection] = useState(persisted);
  const [choosing, setChoosing] = useState(() => persisted.mode !== MODE_ALL);
  // Anchor for shift-click range selection.
  const anchorRef = useRef(null);

  // Re-seed only when the SERVER's selection actually changes — i.e. a compile
  // was queued with a new scope. Comparing by value (not object identity)
  // keeps Firestore's per-snapshot object churn from stomping an edit the user
  // is halfway through making.
  const seedRef = useRef(persisted);
  useEffect(() => {
    if (selectionsEqual(seedRef.current, persisted)) return;
    seedRef.current = persisted;
    setSelection(persisted);
    setChoosing(persisted.mode !== MODE_ALL);
  }, [persisted]);

  const selectedDays = useMemo(
    () => new Set(daysForSelection(selection, allDayKeys)),
    [selection, allDayKeys],
  );

  const applyDays = useCallback(
    (nextSet) => setSelection(selectionFromDays(nextSet, allDayKeys)),
    [allDayKeys],
  );

  // Plain click toggles one day and moves the anchor. Shift-click ADDS every
  // day between the anchor and the target — the "select a range" affordance,
  // which on a 300-day project is the difference between one gesture and 300.
  const toggleDay = useCallback(
    (dayKey, shiftKey = false) => {
      const next = new Set(selectedDays);
      const anchor = anchorRef.current;
      if (shiftKey && anchor && anchor !== dayKey) {
        const i = allDayKeys.indexOf(anchor);
        const j = allDayKeys.indexOf(dayKey);
        if (i !== -1 && j !== -1) {
          const [lo, hi] = i <= j ? [i, j] : [j, i];
          for (let k = lo; k <= hi; k += 1) next.add(allDayKeys[k]);
          applyDays(next);
          return;
        }
      }
      if (next.has(dayKey)) next.delete(dayKey);
      else next.add(dayKey);
      anchorRef.current = dayKey;
      applyDays(next);
    },
    [selectedDays, allDayKeys, applyDays],
  );

  const selectAll = useCallback(() => {
    anchorRef.current = null;
    setSelection(SELECTION_ALL);
  }, []);

  const clearAll = useCallback(() => {
    anchorRef.current = null;
    applyDays(new Set());
  }, [applyDays]);

  // Leaving "choose days" reverts to every day, so the CTA can never be left
  // silently scoped by a panel the user has closed.
  const stopChoosing = useCallback(() => {
    setChoosing(false);
    anchorRef.current = null;
    setSelection(SELECTION_ALL);
  }, []);

  return {
    selection,
    selectedDays,
    allDayKeys,
    choosing,
    startChoosing: useCallback(() => setChoosing(true), []),
    stopChoosing,
    toggleDay,
    selectAll,
    clearAll,
  };
}

export default useDaySelection;
